import { useMutation, useQuery } from '@tanstack/react-query';
import { errorMessage } from '../../lib/api';
import { formatDuration, formatPercent, monthLabel } from '../../lib/dashboard-period';
import { formatMoney } from '../../lib/format';
import { dashboardApi } from '../../lib/resources';
import { ChartCard, Funnel, GroupedBars, HorizontalBars, KpiCard } from '../charts';
import { Button, ErrorNote, Loading } from '../ui';

/**
 * Visão Painel da pipeline (doc 03 §5): KPIs, funil, atendentes, produtos e
 * meses — com os mesmos filtros e o intervalo da tela. Aqui o status não tem
 * padrão: sem filtro de status, conta em andamento, ganhos e perdidos.
 */
export function PipelinePanelView({
  pipelineId,
  filter,
}: {
  pipelineId: string;
  filter: string;
}) {
  const query = useQuery({
    queryKey: ['kanban', 'panel', pipelineId, filter],
    queryFn: () => dashboardApi.pipeline(pipelineId, filter),
  });
  const exporter = useMutation({
    mutationFn: () => dashboardApi.export('pipeline', { pipelineId, filter }),
  });

  if (query.isLoading) return <Loading label="Calculando o painel…" />;
  if (query.isError || !query.data) {
    return <ErrorNote>{errorMessage(query.error, 'Falha ao carregar o painel')}</ErrorNote>;
  }
  const data = query.data;

  return (
    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-6">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {data.period.from || data.period.to
            ? 'Negócios criados no intervalo escolhido.'
            : 'Todos os negócios da pipeline (sem intervalo).'}
        </p>
        <Button onClick={() => exporter.mutate()} disabled={exporter.isPending}>
          {exporter.isPending ? 'Exportando…' : 'Exportar'}
        </Button>
      </div>
      {exporter.error && (
        <ErrorNote>{errorMessage(exporter.error, 'Falha ao exportar')}</ErrorNote>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          label="Criados"
          count={data.kpis.created.count}
          value={data.kpis.created.total}
        />
        <KpiCard
          label="Ganhos"
          tone="emerald"
          count={data.kpis.won.count}
          value={data.kpis.won.total}
        />
        <KpiCard
          label="Perdidos"
          tone="red"
          count={data.kpis.lost.count}
          value={data.kpis.lost.total}
        />
        <KpiCard
          label="Em aberto"
          tone="sky"
          count={data.kpis.open.count}
          value={data.kpis.open.total}
        />
        <KpiCard label="Total" count={data.kpis.total.count} value={data.kpis.total.total} />
      </div>

      <ChartCard title="Funil">
        <Funnel
          stages={data.funnel.map((stage) => ({
            key: stage.stageId,
            name: stage.name,
            color: stage.color,
            entered: stage.entered,
            count: stage.count,
            total: stage.total,
            conversion: formatPercent(stage.conversion),
            avgTime: formatDuration(stage.avgTimeMs),
          }))}
        />
        <p className="mt-3 text-[11px] text-slate-400">
          Conversão: dos que passaram pela etapa, quantos chegaram a uma etapa adiante ou foram
          ganhos a partir dela. Tempo médio: da entrada até a próxima movimentação ou o
          fechamento.
        </p>
      </ChartCard>

      <div className="grid gap-5 lg:grid-cols-3">
        <ChartCard title="Percentual por atendente">
          <HorizontalBars
            items={data.byAttendant.map((item) => ({
              key: item.attendantId ?? 'sem',
              label: item.name,
              value: item.share,
              display: formatPercent(item.share),
              detail: `${item.count} negócio(s) · ${formatMoney(item.total)}`,
            }))}
          />
        </ChartCard>
        <ChartCard title="Atendentes com mais negócios">
          <HorizontalBars
            items={data.topAttendants.map((item) => ({
              key: item.attendantId ?? 'sem',
              label: item.name,
              value: item.count,
              display: `${item.count}`,
            }))}
          />
        </ChartCard>
        <ChartCard title="Produtos com mais negócios">
          <HorizontalBars
            empty="Nenhum produto nos negócios."
            items={data.topProducts.map((item) => ({
              key: item.productId,
              label: item.name,
              value: item.deals,
              display: `${item.deals} negócio(s)`,
              detail: formatMoney(item.total),
            }))}
          />
        </ChartCard>
      </div>

      <ChartCard title="Últimos 12 meses">
        <GroupedBars
          data={data.monthly}
          labelKey="month"
          formatLabel={monthLabel}
          series={[
            { key: 'created', label: 'Criados', color: '#6366f1' },
            { key: 'won', label: 'Ganhos', color: '#10b981' },
            { key: 'lost', label: 'Perdidos', color: '#ef4444' },
          ]}
        />
      </ChartCard>
    </div>
  );
}
